import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LinkButton } from '../../../ui/LinkButton/LinkButton';
import { CustomEmailTemplateModal } from '../../../features/relance/customEmailTemplate/CustomEmailTemplateModal';
import { useCustomEmailTemplateStore } from '../../../stores/customEmailTemplateStore';
import { TranslationKey } from '../../../i18n/translationKeys';
import type { CustomEmailTemplateSetting, TemplateLanguage } from '../../../types/customEmailTemplate';
import { CustomTemplateLanguageToggle } from './CustomTemplateLanguageToggle';
import { toCanonical } from '../../../features/relance/customEmailTemplate/customTemplateMarkers';

interface CustomTemplateToolbarProps {
    /** The custom template's language while the "Personnalisé" chip is active, null otherwise. */
    activeLanguage: TemplateLanguage | null;
    languages: TemplateLanguage[];
    hasTemplate: boolean;
    disabled: boolean;
    onSelectLanguage: (language: TemplateLanguage) => void;
    /** The email being edited, the starting point of a new template. */
    currentSubject: string;
    currentBody: string;
    onCreated: () => void;
}

export const CustomTemplateToolbar: React.FC<CustomTemplateToolbarProps> = ({
    activeLanguage, languages, hasTemplate, disabled, onSelectLanguage, currentSubject, currentBody, onCreated,
}) => {
    const { t, i18n } = useTranslation();
    const template = useCustomEmailTemplateStore(s => s.template);
    const [editor, setEditor] = useState<{ initial: CustomEmailTemplateSetting; language: TemplateLanguage } | null>(null);

    const openEditor = () => {
        const language = activeLanguage ?? (i18n.language === 'en' ? 'EN' : 'FR');
        const prefill = { subject: toCanonical(currentSubject), body: toCanonical(currentBody) };
        const fromEmail = { fr: language === 'FR' ? prefill : null, en: language === 'EN' ? prefill : null };
        setEditor({ initial: hasTemplate && template ? template : fromEmail, language });
    };

    return (
        <div className="flex flex-col items-start gap-s">
            {activeLanguage && languages.length > 1 && (
                <CustomTemplateLanguageToggle value={activeLanguage} languages={languages} onChange={onSelectLanguage} disabled={disabled} />
            )}
            <LinkButton type="button" disabled={disabled} onClick={openEditor} data-testid="email-custom-template-edit-link">
                {t(hasTemplate ? TranslationKey.CustomerFollowUp.Email.CustomTemplate.Edit : TranslationKey.CustomerFollowUp.Email.CustomTemplate.Create)}
            </LinkButton>
            {editor && (
                <CustomEmailTemplateModal
                    initial={editor.initial}
                    initialLanguage={editor.language}
                    onClose={() => setEditor(null)}
                    onSaved={() => { if (!hasTemplate) onCreated(); }}
                />
            )}
        </div>
    );
};
